export default function Home() {
  return (
    <main className="min-h-screen bg-background-50 text-foreground-300">
      {/* 1. Company Title Section */}
      <section className="relative flex flex-col items-center justify-center min-h-[92vh] px-4 md:px-6">
        <div className="flex flex-col items-center">
          <h1 className="font-heading text-[clamp(2.5rem,5.5vw,5rem)] font-light text-foreground-50 tracking-[-0.02em] leading-none text-center">
            Pyrena Tech OU&nbsp;
          </h1>
          <div className="mt-10 md:mt-12 w-16 h-px bg-accent-500/60"></div>
          <p className="mt-8 md:mt-10 font-label text-[0.7rem] md:text-xs tracking-[0.28em] uppercase text-foreground-700 text-center">
            iOS Platform Engineering
          </p>
        </div>
      </section>

      {/* 2. Company Description Section */}
      <section className="relative px-4 md:px-6 py-28 md:py-36 bg-background-100">
        <div className="max-w-[1280px] mx-auto flex flex-col lg:flex-row gap-16 lg:gap-24">
          {/* Left Column */}
          <div className="flex-1 lg:w-[45%]">
            <span className="font-label text-[0.7rem] tracking-[0.2em] uppercase text-accent-500/50">
              About
            </span>
            <h2 className="mt-10 font-heading text-3xl md:text-[2.35rem] font-medium text-foreground-50 leading-[1.15] max-w-[22ch]">
              Engineering Excellence for Modern Platforms
            </h2>
            <p className="mt-10 font-body text-[0.95rem] md:text-base leading-[1.75] text-foreground-300 max-w-[46ch]">
              Pyrena Tech OU builds scalable, maintainable, and high-performance digital software solutions for the iOS platform and related technologies. Every system is designed with a strong emphasis on usability, stability, and long-term technical quality, ensuring that the solutions delivered today remain robust and adaptable well into the future.
            </p>
            <p className="mt-6 font-body text-[0.95rem] md:text-base leading-[1.75] text-foreground-500 max-w-[46ch]">
              The engineering approach is grounded in modern software practices that prioritize clean separation of concerns, systematic performance analysis, and user-centered design. By continuously refining internal workflows and adopting up-to-date engineering standards, the team maintains a disciplined, quality-driven development process that produces reliable, future-ready outcomes.
            </p>
          </div>

          {/* Right Column */}
          <div className="flex-1 lg:w-[55%] relative flex items-start lg:justify-end">
            <div className="relative lg:ml-12">
              <span className="absolute -top-6 -left-2 lg:-top-8 lg:-left-4 font-heading text-[9rem] md:text-[12rem] lg:text-[14rem] font-light text-background-200 leading-none select-none pointer-events-none">
                01
              </span>
              <p className="relative font-heading italic text-[1.05rem] md:text-lg font-light text-foreground-200 leading-[1.7] max-w-[38ch] lg:pl-14 pt-6 lg:pt-8">
                We believe that great software is built through disciplined engineering, thoughtful architecture, and an unwavering commitment to technical quality — not through shortcuts or temporary fixes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Engineering Philosophy Section */}
      <section className="px-4 md:px-6 py-28 md:py-36 bg-background-50">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex flex-col items-center text-center mb-20 md:mb-24">
            <span className="font-heading text-sm text-accent-500/50">&ndash;</span>
            <h2 className="mt-8 font-heading text-3xl md:text-[2.75rem] font-medium text-foreground-50 leading-[1.1] max-w-[28ch]">
              Principles That Guide Our Engineering
            </h2>
            <p className="mt-6 font-body text-[0.9rem] md:text-base text-foreground-600 leading-relaxed max-w-[34ch]">
              A disciplined approach rooted in proven methodologies and continuous improvement
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {/* Principle 01 */}
            <div>
              <span className="font-label text-[0.7rem] tracking-[0.15em] uppercase text-accent-500/60">
                01.
              </span>
              <h3 className="mt-6 font-heading text-xl md:text-2xl font-medium text-foreground-50 leading-[1.2]">
                Clean Architecture
              </h3>
              <p className="mt-5 font-body text-[0.875rem] md:text-[0.9rem] leading-[1.7] text-foreground-600">
                Systems are designed with clear separation of concerns, enabling long-term maintainability and the systematic evolution of complex codebases. Structural integrity is treated as a first-class requirement, not an afterthought.
              </p>
            </div>

            {/* Principle 02 */}
            <div>
              <span className="font-label text-[0.7rem] tracking-[0.15em] uppercase text-accent-500/60">
                02.
              </span>
              <h3 className="mt-6 font-heading text-xl md:text-2xl font-medium text-foreground-50 leading-[1.2]">
                Performance Optimization
              </h3>
              <p className="mt-5 font-body text-[0.875rem] md:text-[0.9rem] leading-[1.7] text-foreground-600">
                Every system layer undergoes rigorous analysis and refinement to ensure responsive, efficient operation across all device configurations. Performance is measured, monitored, and continuously improved throughout the development lifecycle.
              </p>
            </div>

            {/* Principle 03 */}
            <div>
              <span className="font-label text-[0.7rem] tracking-[0.15em] uppercase text-accent-500/60">
                03.
              </span>
              <h3 className="mt-6 font-heading text-xl md:text-2xl font-medium text-foreground-50 leading-[1.2]">
                User-Centered Design
              </h3>
              <p className="mt-5 font-body text-[0.875rem] md:text-[0.9rem] leading-[1.7] text-foreground-600">
                Design decisions are grounded in real usage patterns and genuine user needs. Every interface element serves a clear functional purpose, resulting in intuitive interactions that feel natural and require minimal learning effort.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Services Overview Section */}
      <section className="px-4 md:px-6 py-28 md:py-36 bg-background-100">
        <div className="max-w-[1280px] mx-auto">
          <div className="mb-20 md:mb-24">
            <h2 className="font-heading text-[3rem] md:text-[4.5rem] font-light text-foreground-50 leading-[1.05] max-w-[14ch]">
              What We
              <br />
              <span className="italic font-normal text-foreground-100">Deliver</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-8">
            {/* Service 01 */}
            <div className="border border-background-300 rounded-2xl p-10 md:p-12">
              <h3 className="font-heading text-xl md:text-[1.6rem] font-medium text-foreground-50 leading-snug">
                Scalable Systems
              </h3>
              <p className="mt-5 font-body text-[0.85rem] md:text-sm leading-[1.7] text-foreground-600">
                Architecting solutions that grow seamlessly with increasing demands while maintaining structural integrity and operational stability. Scale is anticipated from the earliest design decisions, not retrofitted under pressure.
              </p>
            </div>

            {/* Service 02 */}
            <div className="border border-background-300 rounded-2xl p-10 md:p-12">
              <h3 className="font-heading text-xl md:text-[1.6rem] font-medium text-foreground-50 leading-snug">
                High-Performance Solutions
              </h3>
              <p className="mt-5 font-body text-[0.85rem] md:text-sm leading-[1.7] text-foreground-600">
                Engineering systems optimized for speed and resource efficiency, delivering responsive experiences without compromise. Every millisecond and every megabyte is accounted for with precision and care.
              </p>
            </div>

            {/* Service 03 */}
            <div className="border border-background-300 rounded-2xl p-10 md:p-12">
              <h3 className="font-heading text-xl md:text-[1.6rem] font-medium text-foreground-50 leading-snug">
                Technical Excellence
              </h3>
              <p className="mt-5 font-body text-[0.85rem] md:text-sm leading-[1.7] text-foreground-600">
                Upholding rigorous engineering standards through systematic code review, comprehensive testing strategies, and continuous refinement of development workflows. Quality is embedded in every stage of the process.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Contact Section */}
      <section className="relative px-4 md:px-6 py-28 md:py-32 bg-background-50">
        <div className="absolute top-0 left-4 right-4 md:left-6 md:right-6 h-px bg-background-300"></div>
        <div className="max-w-[1280px] mx-auto pt-28 md:pt-32 flex flex-col lg:flex-row gap-16 lg:gap-24">
          {/* Left Column */}
          <div className="lg:w-[30%]">
            <h2 className="font-heading text-[4rem] md:text-[5.5rem] font-thin text-foreground-50 leading-none tracking-[-0.02em]">
              Contact
            </h2>
            <div className="mt-8 w-10 h-px bg-accent-500/50"></div>
          </div>

          {/* Right Column */}
          <div className="lg:w-[70%] grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-12">
            {/* Address */}
            <div>
              <span className="font-label text-[0.65rem] tracking-[0.18em] uppercase text-foreground-700">
                Address
              </span>
              <address className="mt-5 font-body text-[0.9rem] md:text-[0.95rem] leading-[1.9] text-foreground-300 not-italic">
                Sepapaja tn 6<br />
                Tallinn<br />
                15551<br />
                Estonia
              </address>
            </div>

            {/* Phone */}
            <div>
              <span className="font-label text-[0.65rem] tracking-[0.18em] uppercase text-foreground-700">
                Phone
              </span>
              <p className="mt-5 font-body text-[0.9rem] md:text-[0.95rem] leading-[1.9] text-foreground-300">
                +372 58989460
              </p>
            </div>

            {/* Email */}
            <div>
              <span className="font-label text-[0.65rem] tracking-[0.18em] uppercase text-foreground-700">
                Email
              </span>
              <p className="mt-5 font-body text-[0.9rem] md:text-[0.95rem] leading-[1.9] text-foreground-300">
                support@pyrenatech.com
              </p>
            </div>
          </div>
        </div>

        {/* Bottom rule */}
        <div className="max-w-[1280px] mx-auto mt-24 md:mt-28">
          <div className="h-px bg-background-200"></div>
        </div>
      </section>
    </main>
  );
}